<script>
  import { getContext, onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { globalHistory, Link, link, useLocation } from 'svelte-navigator';
  import { Badge } from '@sveltestrap/sveltestrap';

  import { Capability } from '@client/components/capability';
  import { DomainSwitcher } from '@client/components/domain-switcher';
  import { Icon } from '@client/components/icon';
  import { NotificationsSection } from '@client/components/notifications-section';
  import { Porthole } from '@client/components/porthole';

  import { desktopNotificationsEnabled } from '@client/stores/notifications';
  import { hasCapability, userStore } from '@client/stores/user';

  import { LocalStorage } from '@client/utils/local-storage';

  import { deepCopy } from '@common/utils/objects';

  import { version } from '@root/package.json';

  import sidebarConf from './config.json';
  import { sidebarStore } from './sidebar.store';

  const { basepath } = getContext('config');

  const location = useLocation();

  export let collapsed = false;
  export let interacting = true;
  export let mobile = false;

  let sidebarConfig = deepCopy(sidebarConf);
  let parentRoute = null;
  let selectedDomain = '';
  let sections = [];

  const getProps = ({ href, location }) => {
    const isActive = href === '/' || `${basepath}${location.pathname}/`.includes(`${href}/`);

    if (isActive) {
      return {
        class: 'active'
      };
    }

    return {};
  };

  const setPageDetails = ({ pathname }) => {
    const path = basepath;
    const index = !path?.length || path?.length === 1 ? 1 : 2;

    parentRoute = pathname.split('/')[index];

    showUserMenu = false;

    sections = sections.map((config) => {
      config.expanded = parentRoute === config.base;
      return config;
    });
  };

  let timer = null;

  const sidebarOpen = () => {
    timer = setTimeout(() => {
      if (collapsed && !interacting) {
        interacting = true;
        clearTimeout(timer);
        timer = null;
        sidebarStore.set({ interacting });
      }
    }, 300);
  };

  const sidebarClose = () => {
    if (timer) {
      clearTimeout(timer);
    }

    if (mobile) {
      translateX = 0;
      backdropOpacity = 0;
      collapsed = true;
      interacting = false;
    } else {
      translateX = 298;
      backdropOpacity = 0;
    }

    if (collapsed && interacting) {
      interacting = false;
      sidebarStore.set({ interacting });
    }
  };

  let showUserMenu = false;

  const toggleUserMenu = () => {
    showUserMenu = !showUserMenu;
  };

  export let maxWidth = 298;
  export let showBackdrop = true;
  export let disableDrag = false;

  let dragging = false;
  let translateX = 0;
  let movementThrow = 0;
  let backdropOpacity = 0;
  let handleRef = null;

  const onTouchStart = (e) => {
    e.preventDefault();

    dragging = true;
    interacting = true;
  };

  const onTouchEnd = () => {
    if (canInteract) {
      dragging = false;

      if (movementThrow > 8) {
        open();
        return;
      }

      if (movementThrow < -8) {
        close();
        return;
      }

      if (translateX > maxWidth / 2) {
        open();
      } else {
        close();
      }
    }
  };

  const onTouchMove = (e) => {
    if (canInteract) {
      const touch = Array.from(e.touches)[0];

      if (touch) {
        move({
          x: Math.round(touch.clientX),
          movementX: 0
        });
      }
    }
  };

  const move = ({ x, movementX }) => {
    movementThrow = movementX;

    if (x <= maxWidth && x >= 0) {
      translateX = x;
      backdropOpacity = x / maxWidth;
    } else if (x > maxWidth) {
      open();
    } else {
      close();
    }
  };

  const open = () => {
    interacting = true;
    collapsed = false;

    translateX = maxWidth;

    if (mobile) {
      backdropOpacity = 1;
    }
  };

  const close = () => {
    if (mobile) {
      translateX = 0;
      backdropOpacity = 0;

      setTimeout(() => {
        collapsed = true;
      }, 60);
    } else {
      translateX = 298;
      backdropOpacity = 0;
    }

    interacting = false;
  };

  const logout = () => {
    $userStore.token = null;
    LocalStorage.remove('user');
    sidebarClose();

    window.location.href = `https://app.opensense.com/logout`;
  };

  onMount(async () => {
    setPageDetails(window.location);

    globalHistory.listen(({ location }) => setPageDetails(location));

    if (mobile) {
      handleRef.addEventListener('touchstart', onTouchStart);
      document.addEventListener('touchmove', onTouchMove);
      document.addEventListener('touchend', onTouchEnd);
    }
  });

  onDestroy(() => {
    if (mobile) {
      handleRef.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    }
  });

  $: canInteract = !disableDrag && dragging;
  $: !interacting && collapsed && close();
  $: interacting && !collapsed && open();

  $: if (!mobile) {
    maxWidth = 298;
    translateX = 298;
    backdropOpacity = 0;
  }

  $: if (mobile) {
    maxWidth = 298;
    translateX = 0;
  }

  $: {
    if ($userStore) {
      sidebarConfig = deepCopy(sidebarConf);

      sections = Object.keys(sidebarConfig)
        .map((key) => {
          const config = sidebarConfig[key];

          // remove access to "user" features if the user is currently
          // managing another domain.
          // Future Kieran: update this check with a role enum
          if ($userStore?.isManaging === true && config.role === 'user') {
            return false;
          }

          config.items = config.items
            .map((item) => {
              return item.permission && hasCapability('read', item.permission) && item;
            })
            .filter(Boolean);

          return config.items.length && config;
        })
        .filter(Boolean);

      selectedDomain = $userStore.domain;
    }
  }

  $: canSwitchDomains = $userStore?.domaincount >= 1 || $userStore?.isStaff;
</script>

{#if mobile}
  <div
    bind:this={handleRef}
    id="handle"
    class="handle"
    style={`transform: translate3d(${translateX}px, 0, 0); ${dragging ? 'transition: none' : ''}`}
  >
    <slot name="handle" />
  </div>

  <slot name="backdrop">
    {#if showBackdrop}
      <div
        style="opacity: {backdropOpacity};"
        class="backdrop"
        class:hide={!backdropOpacity}
        on:click={close}
        role="button"
        tabindex="0"
      />
    {/if}
  </slot>
{/if}

<nav
  id="sidebar"
  class:collapsed={!interacting && collapsed && !mobile}
  class:mobile
  on:mouseenter={sidebarOpen}
  on:mouseleave={sidebarClose}
  style="transform: translate3d({translateX - maxWidth}px, 0, 0); {dragging ? 'transition: none' : ''}"
>
  <div class="sidebar-container">
    <div class="sidebar-header">
      <a class="ui-toggle" href="https://app.opensense.com/user/#/dashboard">Switch to Legacy UI</a>
      <img
        class:emblem={!interacting && collapsed && !mobile}
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        alt="Opensense"
      />
      <div class="version">{$format('label.BUILD_VERSION', { values: { version } })}</div>
    </div>
    <div class="sidebar-contents">
      <div class="top">
        {#each sections as section}
          <div class="section" class:expanded={section.expanded}>
            <div
              class="heading"
              role="button"
              tabindex="0"
              class:active={parentRoute === section.base}
              data-testid="section-{section.base.replace('/', '-')}"
              on:click={() => (section.expanded = !section.expanded)}
            >
              <Icon
                class="p-2"
                name={section.icon}
                size={17.5}
                background={parentRoute === section.base ? 'info' : undefined}
                --bs-info-rgb="80, 192, 175"
              />
              <span>{$format(section.title)}</span>
              <span class="toggle">
                <Icon name={section.expanded ? 'chevron-up' : 'chevron-down'} />
              </span>
            </div>
            <ul class:active={parentRoute === section.base}>
              {#each section.items as item}
                <li data-testid="section-item-{item.path.replace('/', '-')}">
                  <Link to={item.path} {getProps} on:click={sidebarClose}>
                    <span>{$format(item.label)}</span>
                  </Link>
                </li>
              {/each}
            </ul>
          </div>
        {/each}
      </div>
      <div class="bottom">
        {#if $desktopNotificationsEnabled}
          <div class="section">
            <div class="heading">
              <NotificationsSection collapsed={!interacting && collapsed && !mobile} />
            </div>
          </div>
        {/if}
        <div class="section">
          <div class="heading">
            <a href="https://help.opensense.com" target="_blank">
              <Icon name="life-buoy" size={17.5} class="p-2" />
              <span>{$format('label.SUPPORT')}</span>
            </a>
          </div>
        </div>
        <div class="user" role="button" tabindex="0" on:click={toggleUserMenu}>
          <Porthole
            details={{
              photoUrl: '',
              email: $userStore.currentUser.email
            }}
          />
          <div class="user-details">
            <div class="name">{$userStore.currentUser.name}</div>
            <div class="company">{$userStore?.domain}</div>

            {#if $userStore?.isManaging}
              <Badge pill color="danger">{$format('label.MANAGING_DOMAIN')}</Badge>
            {/if}
          </div>
          <Icon name="chevrons-up-down" tabindex="0" />
        </div>
      </div>
    </div>

    {#if !collapsed || interacting}
      <div class="user-popover" class:show={showUserMenu}>
        <div class="user">
          <Porthole
            details={{
              photoUrl: '',
              email: $userStore.currentUser.email
            }}
          />
          <div class="user-details">
            <div class="name">{$userStore.currentUser.name}</div>
            <div class="company">
              {#if canSwitchDomains}
                <DomainSwitcher variant="sidebar" nav onSwitched={() => (showUserMenu = false)}>
                  <div slot="toggle">
                    <div>{selectedDomain}</div>
                    <small>{$format('label.CHANGE_DOMAIN')}</small>
                  </div>
                </DomainSwitcher>
              {:else}
                {selectedDomain}
              {/if}
            </div>
          </div>
          <Icon name="chevrons-down-up" role="button" tabindex="0" on:click={toggleUserMenu} />
        </div>
        <div class="user-links">
          <ul>
            {#if !$userStore?.isManaging}
              <li class:active={$location.pathname === '/settings'}>
                <a href="{basepath}/settings" use:link on:click={sidebarClose}>
                  <Icon name="settings" />
                  <span>{$format('label.MY_SETTINGS')}</span>
                </a>
              </li>
            {/if}

            {#if !canSwitchDomains}
              <li>
                <a href="https://help.opensense.com" target="_blank">
                  <Icon name="life-buoy" />
                  <span>{$format('label.SUPPORT')}</span>
                </a>
              </li>
            {/if}

            <Capability action="manage" subject="manage:domains">
              <li class:active={$location.pathname === '/manage/domains'}>
                <a href="{basepath}/manage/domains" use:link on:click={sidebarClose}>
                  <Icon name="server" />
                  <span>{$format('label.MANAGE_DOMAINS')}</span>
                </a>
              </li>
            </Capability>

            <Capability action="manage" subject="staff">
              <li class:active={$location.pathname === '/staff'}>
                <a href="{basepath}/staff" use:link on:click={sidebarClose}>
                  <Icon name="coffee" />
                  <span>{$format('label.STAFF_TOOLS')}</span>
                </a>
              </li>
            </Capability>
          </ul>
          <div on:click={logout} role="button" tabindex="0">
            <Icon name="log-out" />
            <span>
              {$format('label.SIGN_OUT')}
              <small>{$userStore.currentUser.email}</small>
            </span>
          </div>
        </div>
      </div>
    {/if}
  </div>
</nav>

<style lang="scss" src="./sidebar.scss"></style>
